import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DevelopmentAnalyticsPanel = ({ filters }) => {
  const analyticsData = {
    trainingROI: {
      totalInvestment: 0,
      productivityGain: 0,
      roi: 0,
      trend: '0%'
    },
    performanceTrends: {
      avgRating: 0.0,
      improvement: '+0.0',
      topPerformers: 0,
      needsImprovement: 0
    },
    successionPipeline: {
      readyNow: 0,
      ready1Year: 0,
      ready2Years: 0,
      healthScore: 0
    },
    workforcePlanning: {
      retirementRisk: 'Low',
      skillGaps: 0,
      talentRetention: 0,
      predictedTurnover: 0
    }
  };

  const departmentPerformance = [];

  const trainingImpact = [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Icon name="TrendingUp" size={28} className="text-emerald-600" />
              Development Analytics
            </h2>
            <p className="text-slate-600 mt-1">Insights into training ROI, performance trends, and workforce planning</p>
          </div>
          <Button variant="primary" iconName="Download">
            Export Analytics Report
          </Button>
        </div>
      </div>
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Training ROI */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <Icon name="DollarSign" size={24} className="text-white" />
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg">
              <Icon name="TrendingUp" size={14} />
              <span className="text-xs font-semibold">{analyticsData?.trainingROI?.trend}</span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-slate-600 mb-2">Training ROI</h3>
          <p className="text-3xl font-bold text-emerald-600 mb-1">{analyticsData?.trainingROI?.roi}%</p>
          <div className="text-xs text-slate-600 space-y-1">
            <p>Investment: K{analyticsData?.trainingROI?.totalInvestment?.toLocaleString()}</p>
            <p>Gain: K{analyticsData?.trainingROI?.productivityGain?.toLocaleString()}</p>
          </div>
        </div>

        {/* Performance Trends */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Icon name="BarChart3" size={24} className="text-white" />
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg">
              <Icon name="TrendingUp" size={14} />
              <span className="text-xs font-semibold">{analyticsData?.performanceTrends?.improvement}</span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-slate-600 mb-2">Avg Performance Rating</h3>
          <p className="text-3xl font-bold text-blue-600 mb-1">{analyticsData?.performanceTrends?.avgRating}</p>
          <div className="text-xs text-slate-600 space-y-1">
            <p>Top Performers: {analyticsData?.performanceTrends?.topPerformers}</p>
            <p>Needs Improvement: {analyticsData?.performanceTrends?.needsImprovement}</p>
          </div>
        </div>

        {/* Succession Pipeline */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Icon name="Users" size={24} className="text-white" />
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-lg">
              <Icon name="CheckCircle" size={14} />
              <span className="text-xs font-semibold">Healthy</span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-slate-600 mb-2">Succession Health Score</h3>
          <p className="text-3xl font-bold text-purple-600 mb-1">{analyticsData?.successionPipeline?.healthScore}%</p>
          <div className="text-xs text-slate-600 space-y-1">
            <p>Ready Now: {analyticsData?.successionPipeline?.readyNow}</p>
            <p>Ready 1-2 Years: {analyticsData?.successionPipeline?.ready1Year + analyticsData?.successionPipeline?.ready2Years}</p>
          </div>
        </div>

        {/* Workforce Planning */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Icon name="Activity" size={24} className="text-white" />
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded-lg">
              <Icon name="AlertCircle" size={14} />
              <span className="text-xs font-semibold">{analyticsData?.workforcePlanning?.retirementRisk}</span>
            </div>
          </div>
          <h3 className="text-sm font-medium text-slate-600 mb-2">Talent Retention Rate</h3>
          <p className="text-3xl font-bold text-amber-600 mb-1">{analyticsData?.workforcePlanning?.talentRetention}%</p>
          <div className="text-xs text-slate-600 space-y-1">
            <p>Predicted Turnover: {analyticsData?.workforcePlanning?.predictedTurnover}%</p>
            <p>Skill Gaps: {analyticsData?.workforcePlanning?.skillGaps}</p>
          </div>
        </div>
      </div>
      {/* Department Performance Comparison */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Icon name="Building" size={24} className="text-slate-700" />
          Department Performance Comparison
        </h3>
        {departmentPerformance?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="Building" size={40} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Department Data Available</h3>
            <p className="text-slate-600">Department performance data will appear here once reviews and training are completed</p>
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Avg Performance Rating
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Training Completion
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Succession Readiness
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Overall Health
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {departmentPerformance?.map((dept, idx) => {
                const overallHealth = Math.round((dept?.avgRating / 5 * 100 + dept?.trainingCompletion + dept?.successionReady) / 3);
                return (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{dept?.department}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-blue-600">{dept?.avgRating}</span>
                        <span className="text-sm text-slate-500">/5.0</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden max-w-[100px]">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
                            style={{ width: `${dept?.trainingCompletion}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-slate-700">{dept?.trainingCompletion}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden max-w-[100px]">
                          <div
                            className="h-full bg-gradient-to-r from-purple-500 to-purple-600"
                            style={{ width: `${dept?.successionReady}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-slate-700">{dept?.successionReady}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                        overallHealth >= 85 ? 'bg-emerald-100 text-emerald-700' :
                        overallHealth >= 70 ? 'bg-blue-100 text-blue-700': 'bg-amber-100 text-amber-700'
                      }`}>
                        {overallHealth >= 85 ? <Icon name="CheckCircle" size={14} /> :
                         overallHealth >= 70 ? <Icon name="Info" size={14} /> :
                         <Icon name="AlertCircle" size={14} />}
                        {overallHealth}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
      {/* Training Impact Analysis */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Icon name="Award" size={24} className="text-slate-700" />
          Training Impact Analysis
        </h3>
        {trainingImpact?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="Award" size={40} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Training Impact Data</h3>
            <p className="text-slate-600">Training impact metrics will be calculated once programs are completed</p>
          </div>
        ) : (
        <div className="space-y-4">
          {trainingImpact?.map((program, idx) => (
            <div key={idx} className="border border-slate-200 rounded-lg p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-bold text-slate-900 mb-1">{program?.program}</h4>
                  <p className="text-sm text-slate-600">{program?.participants} participants completed</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600 mb-1">Performance Improvement</p>
                  <p className="text-2xl font-bold text-emerald-600">+{program?.improvement}%</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-600 mb-1">Before Training</p>
                  <p className="text-lg font-bold text-slate-900">{program?.avgRatingBefore}</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-700 mb-1">After Training</p>
                  <p className="text-lg font-bold text-emerald-900">{program?.avgRatingAfter}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
      {/* Predictive Insights */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
            <Icon name="Sparkles" size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Predictive Workforce Planning Insights</h3>
            <div className="space-y-2 text-sm text-slate-700">
              <p>• <strong>Retirement Risk:</strong> 12 employees retiring in next 24 months - succession plans in place for 9 positions</p>
              <p>• <strong>Skill Gap Analysis:</strong> 5 critical skill gaps identified in Operations department - training programs recommended</p>
              <p>• <strong>Turnover Prediction:</strong> 8% predicted turnover rate (below industry average of 12%)</p>
              <p>• <strong>High-Potential Pipeline:</strong> 23 employees identified for leadership development - 18 enrolled in programs</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevelopmentAnalyticsPanel;